import { BoxGeometry, Color, type ColorRepresentation, EdgesGeometry, Group, Material, Mesh, MeshBasicMaterial, Object3D } from "three";
import animate from "./animate";
import delay from "./delay";
import { easeInOutCubic, easeOutCubic } from "./easing";
import throttle from "./throttle";
import Colors from "@cosine/lib/Colors";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import IsoBlock from "@cosine/components/onboarding/welcome/IsoBlock";

export interface IsoPromptBlockOptions {
  color: ColorRepresentation,
  highlightColor: ColorRepresentation,
  material?: Material,
}

export interface IsoPromptBlockColorOptions {
  fromColor?: ColorRepresentation,
  toColor?: ColorRepresentation,
}

export interface IsoPromptBlockSpinSegmentOptions extends IsoPromptBlockColorOptions {
  duration: number,
}

export type IsoPromptBlockState = "User" | "System";

export default class IsoPromptBlock {
  blockGeometry = new BoxGeometry(1, 1, 1);
  block: IsoBlock;
  segmentGeometry: BoxGeometry;
  meshGroup = new Group();
  segmentMeshGroup = new Group();
  segmentMeshes: Array<Mesh>;
  segmentStateMap: Array<boolean> = [
    false,
    false,
    false,
    false,
  ];
  topIllusionBlock: IsoBlock;
  bottomIllusionBlock: IsoBlock;
  color: ColorRepresentation;
  highlightColor: ColorRepresentation;
  state: IsoPromptBlockState = "User";

  constructor (
    parent: Object3D,
    options: IsoPromptBlockOptions = {
      color: Colors.purple300,
      highlightColor: Colors.teal300,
    },
  ) {
    this.color = options.color;
    this.highlightColor = options.highlightColor;

    this.block = new IsoBlock(this.meshGroup, {
      color: this.color,
    });

    this.topIllusionBlock = new IsoBlock(this.meshGroup, {
      color: this.color,
    });
    this.topIllusionBlock.visible = false;
    this.bottomIllusionBlock = new IsoBlock(this.meshGroup, {
      color: this.color,
    });
    this.bottomIllusionBlock.visible = false;

    const meshCount = 4;
    this.segmentGeometry = new BoxGeometry(1, 1 / meshCount, 1);

    const segmentEdgeGeometry = new EdgesGeometry(this.segmentGeometry);
    const edgeLineMaterial = new LineMaterial({
      color: Colors.black,
      linewidth: 1,
    });

    this.segmentMeshes = Array(meshCount).fill(0)
      .map((_, i) => {
        const material = new MeshBasicMaterial({
          color: this.color,
          polygonOffset: true,
          polygonOffsetFactor: 1,
          polygonOffsetUnits: 1,
        });

        // TODO: dispose
        const mesh = new Mesh(this.segmentGeometry, material);
        mesh.visible = false;
        const edgeLineGeometry = new LineSegmentsGeometry().setPositions(Array.from(segmentEdgeGeometry.attributes.position.array));
        const edges = new LineSegments2(edgeLineGeometry, edgeLineMaterial);
        mesh.add(edges);

        if (i === meshCount - 1) {
          mesh.castShadow = false;
        }

        mesh.position.y = 0.5 / meshCount + i * 1 / meshCount;

        return mesh;
      });

    this.segmentMeshGroup.add(...this.segmentMeshes);
    this.meshGroup.add(this.segmentMeshGroup);

    parent.add(this.meshGroup);
  }

  async animateIn (render: () => void): Promise<void> {
    await animate((t) => {
      this.meshGroup.position.y = -1 + t;
      render();
    }, {
      easing: easeOutCubic,
      duration: 750,
    });
  }

  async animateOut (render: () => void): Promise<void> {
    await animate((t) => {
      this.meshGroup.position.y = 0 - t * 1.1;
      render();
    }, {
      easing: easeOutCubic,
      duration: 750,
    });
  }

  async animateThinking (render: () => void): Promise<void> {
    await this.animateUp(render);
    await this.animateUp(render);
  }

  async animateAnswering (handler: ({
    index,
  }: { index: number }) => void, render: () => void, {
    count,
  }: { count?: number } = {
    count: 4,
  }): Promise<void> {
    const colorOptions = this.state === "User"
      ? {
        fromColor: this.color,
        toColor: this.highlightColor,
      }
      : {};
    this.state = "System";

    const animateSegmentAtIndex = async (i: number): Promise<void> => {
      await this.animateSegmentAtIndex(i, render, {
        ...colorOptions,
        duration: 350,
      });
    };

    if (count === 2) {
      await Promise.all([
        animateSegmentAtIndex(0),
        animateSegmentAtIndex(1),
        handler({
          index: 0,
        }),
      ]);
      await Promise.all([
        animateSegmentAtIndex(2),
        animateSegmentAtIndex(3),
        handler({
          index: 1,
        }),
      ]);
    } else {
      for (const index of this.segmentMeshes.keys()) {
        await Promise.all([
          animateSegmentAtIndex(index),
          handler({
            index,
          }),
        ]);
      }
    }
  }

  async animateResponse (render: () => void): Promise<void> {
    const colorOptions = this.state === "System"
      ? {
        fromColor: this.highlightColor,
        toColor: this.color,
      }
      : {};
    this.state = "User";

    await this.animateUp(render, colorOptions);
  }

  private async animateUp (render: () => void, options: IsoPromptBlockColorOptions = {}): Promise<void> {
    this.segmentMeshes.forEach(async (_, i) => {
      await delay(100 * i);
      await this.animateSegmentAtIndex(
        i,
        render,
        {
          duration: 350,
          ...options,
        },
      );
    });

    await delay(750);
  }

  async animateDown (render: () => void): Promise<void> {
    const throttleRender = throttle(render, 1000 / 120);
    const colorOptions = this.state === "System"
      ? {
        fromColor: this.highlightColor,
        toColor: this.color,
      }
      : {};

    this.segmentMeshes.forEach(async (_, i, arr) => {
      const j = arr.length - (i + 1);

      await delay(100 * i);
      await this.animateSegmentAtIndex(
        j,
        throttleRender,
        {
          duration: 350,
          ...colorOptions,
        },
      );
    });

    await delay(100 * (this.segmentMeshes.length - 1) + 350);
  }

  private toggleSegmentState (index: number, isAnimating: boolean) {
    if (this.segmentStateMap[index] === isAnimating) return;

    this.segmentStateMap[index] = isAnimating;
    this.segmentMeshes[index].visible = isAnimating;
    this.block.visible = this.segmentStateMap.every((isAnimating) => !isAnimating);

    if (this.block.visible) {
      (this.block.material as MeshBasicMaterial).color = (this.segmentMeshes[0].material as MeshBasicMaterial).color;
    }

    if (!this.block.mesh.visible && !this.segmentStateMap[this.segmentStateMap.length - 1]) {
      this.topIllusionBlock.visible = true;
      (this.topIllusionBlock.material as MeshBasicMaterial).color = (this.segmentMeshes[this.segmentMeshes.length - 1].material as MeshBasicMaterial).color;
      this.topIllusionBlock.height = (this.segmentMeshes.length - (1 + this.segmentStateMap.findLastIndex((isAnimating) => isAnimating))) / this.segmentMeshes.length;
      this.topIllusionBlock.y = 1 - this.topIllusionBlock.height;
    } else {
      this.topIllusionBlock.visible = false;
    }

    if (!this.block.visible && !this.segmentStateMap[0]) {
      this.bottomIllusionBlock.visible = true;
      (this.bottomIllusionBlock.material as MeshBasicMaterial).color = (this.segmentMeshes[0].material as MeshBasicMaterial).color;
      this.bottomIllusionBlock.height = this.segmentStateMap.findIndex((isAnimating) => isAnimating) / this.segmentMeshes.length;
      this.bottomIllusionBlock.y = 0;
    } else {
      this.bottomIllusionBlock.visible = false;
    }
  }

  private async animateSegmentAtIndex (index: number, render: () => void, options: IsoPromptBlockSpinSegmentOptions): Promise<void> {
    const segmentMesh = this.segmentMeshes[index];

    this.toggleSegmentState(index, true);

    await animate((t) => {
      segmentMesh.rotation.y = -Math.PI / 2 * t;

      // TODO: revisit to fix color issue when toggling state
      if (options.fromColor && options.toColor) {
        (segmentMesh.material as MeshBasicMaterial).color.lerpColors(
          new Color(options.fromColor),
          new Color(options.toColor),
          t,
        );
      }

      render();
    }, {
      duration: options.duration,
      easing: easeInOutCubic,
    });

    this.toggleSegmentState(index, false);
  }

  dispose () {
    this.meshGroup.removeFromParent();
    this.block.dispose();
    this.topIllusionBlock.dispose();
    this.bottomIllusionBlock.dispose();
    this.segmentMeshes.forEach((segmentMesh) => {
      (segmentMesh.material as MeshBasicMaterial).dispose();
      segmentMesh.geometry.dispose();
    });
    this.segmentGeometry.dispose();
  }
}
